/**
 * Noise synthesis for the white noise generator: white, pink and brown noise as arrays of
 * samples, made loopable so an audio buffer can repeat without a click.
 *
 * The three colours differ in how power falls with frequency. White noise has equal
 * power at every frequency (flat), pink falls 3 dB per octave (power ∝ 1/f), and brown
 * (red) noise falls 6 dB per octave (power ∝ 1/f²), which is why it sounds deeper.
 *
 * The generator takes a `random` function so tests can seed it; the widget passes
 * `Math.random` from an event handler, never during render.
 */

export const NOISE_KINDS = ["white", "pink", "brown"] as const;
export type NoiseKind = (typeof NOISE_KINDS)[number];

/** RMS level every colour is scaled to, well below full scale. */
export const TARGET_RMS = 0.18;
const PEAK_LIMIT = 0.9;

type Random = () => number;

function whiteSample(random: Random): number {
  return random() * 2 - 1;
}

function fillWhite(data: Float32Array, random: Random): void {
  for (let i = 0; i < data.length; i += 1) {
    data[i] = whiteSample(random);
  }
}

/** Paul Kellet's refined pink filter: within about 0.05 dB of 1/f above ~10 Hz at 44.1 kHz. */
function fillPink(data: Float32Array, random: Random): void {
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < data.length; i += 1) {
    const white = whiteSample(random);
    b0 = 0.998_86 * b0 + white * 0.055_517_9;
    b1 = 0.993_32 * b1 + white * 0.075_075_9;
    b2 = 0.969 * b2 + white * 0.153_852;
    b3 = 0.8665 * b3 + white * 0.310_485_6;
    b4 = 0.55 * b4 + white * 0.532_952_2;
    b5 = -0.7616 * b5 - white * 0.016_898;
    data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
    b6 = white * 0.115_926;
  }
}

/** A leaky integral of white noise: −6 dB per octave, kept from drifting by the leak. */
function fillBrown(data: Float32Array, random: Random): void {
  let last = 0;
  for (let i = 0; i < data.length; i += 1) {
    last = (last + 0.02 * whiteSample(random)) / 1.02;
    data[i] = last;
  }
}

function rms(data: Float32Array): number {
  let sum = 0;
  for (const value of data) {
    sum += value * value;
  }
  return Math.sqrt(sum / Math.max(1, data.length));
}

/** Scales to `TARGET_RMS`, then down again if a rare peak would pass `PEAK_LIMIT`. */
export function normalise(data: Float32Array): Float32Array {
  const level = rms(data);
  if (level === 0) {
    return data;
  }
  let gain = TARGET_RMS / level;
  let peak = 0;
  for (const value of data) {
    peak = Math.max(peak, Math.abs(value));
  }
  if (peak * gain > PEAK_LIMIT) {
    gain = PEAK_LIMIT / peak;
  }
  for (let i = 0; i < data.length; i += 1) {
    data[i] = (data[i] ?? 0) * gain;
  }
  return data;
}

/**
 * `length` samples that loop without a click.
 *
 * Noise is generated `fade` samples longer, and the start is an equal-power crossfade
 * from the continuation of the tail (samples `length…length+fade`) into the head. The
 * first sample then follows the last as the generator itself would have produced it, so
 * there is no jump at the seam, and equal-power weights (cosine and sine) keep the
 * level steady through the fade, since the two parts are uncorrelated.
 */
export function makeLoopable(source: Float32Array, fade: number): Float32Array {
  const length = source.length - fade;
  const out = new Float32Array(length);
  out.set(source.subarray(0, length));
  for (let i = 0; i < fade; i += 1) {
    const angle = ((i + 0.5) / fade) * (Math.PI / 2);
    out[i] =
      (source[i] ?? 0) * Math.sin(angle) +
      (source[length + i] ?? 0) * Math.cos(angle);
  }
  return out;
}

/** A loopable, level-matched buffer of noise. */
export function makeNoise(
  kind: NoiseKind,
  length: number,
  random: Random,
  fade = Math.min(4096, Math.floor(length / 4))
): Float32Array {
  const raw = new Float32Array(length + fade);
  if (kind === "white") {
    fillWhite(raw, random);
  } else if (kind === "pink") {
    fillPink(raw, random);
  } else {
    fillBrown(raw, random);
  }
  return normalise(makeLoopable(raw, fade));
}

/** Low-pass cutoff in Hz for a "softness" value from 0 (open) to 100 (muffled). */
export function softnessToCutoff(softness: number): number {
  const clamped = Math.min(100, Math.max(0, softness));
  const open = 20_000;
  const closed = 400;
  return Math.round(open * (closed / open) ** (clamped / 100));
}

/** Timer lengths in minutes; 0 means no timer. */
export const TIMER_CHOICES = [0, 15, 30, 45, 60, 90, 120] as const;
/** Seconds over which the sound fades out before the timer ends. */
export const FADE_OUT_SECONDS = 8;

/** m:ss, or h:mm:ss from an hour up. */
export function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const two = (value: number) => String(value).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}
